import React, { useMemo } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, AlertTriangle, X } from 'lucide-react';
import { useWebsites } from '@/hooks/useWebsites';
import { useHostings } from '@/hooks/useHostings';
import { useWorkspace } from '@/hooks/useWorkspace';
import { cn } from '@/lib/utils';

interface ExpiryItem {
  id: string;
  name: string;
  type: 'domena' | 'hosting';
  daysUntilExpiry: number;
  category?: string;
}

const ExpiryAlertBanner: React.FC = () => {
  const { websites: allWebsites } = useWebsites();
  const { workspace } = useWorkspace();
  // Alerty wygasania podążają za globalnym workspace — w trybie Firma nie
  // mrugają domeny Sponsoraki (zespół i tak dostaje z RLS samą firmę).
  const websites = useMemo(
    () => allWebsites.filter(w => (w.workspace || 'firma') === workspace),
    [allWebsites, workspace],
  );
  const { hostings } = useHostings();

  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return Infinity;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return Infinity;

    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const expiryItems = useMemo(() => {
    const items: ExpiryItem[] = [];

    // Zbierz wygasłe/wygasające domeny
    websites.forEach(website => {
      if (website.expiry_date) {
        const days = getDaysUntilExpiry(website.expiry_date);
        // Pokazuj tylko wygasłe lub wygasające w ciągu 14 dni
        if (days <= 14) {
          items.push({
            id: website.id,
            name: website.name,
            type: 'domena',
            daysUntilExpiry: days,
            category: website.category
          });
        }
      }
    });

    // Zbierz wygasłe/wygasające hostingi
    hostings.forEach(hosting => {
      if (hosting.expiry_date) {
        const days = getDaysUntilExpiry(hosting.expiry_date);
        // Pokazuj tylko wygasłe lub wygasające w ciągu 14 dni
        if (days <= 14) {
          items.push({
            id: hosting.id,
            name: hosting.name,
            type: 'hosting',
            daysUntilExpiry: days
          });
        }
      }
    });

    // Sortuj: najpierw wygasłe (od najdłuższego czasu), potem wygasające (od najkrótszego czasu)
    return items.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  }, [websites, hostings]);

  // Podziel na wygasłe i wygasające
  const expiredItems = expiryItems.filter(item => item.daysUntilExpiry < 0);
  const expiringSoonItems = expiryItems.filter(item => item.daysUntilExpiry >= 0);

  if (expiryItems.length === 0) {
    return null; // Nie pokazuj bannera jeśli nie ma wygasłych/wygasających pozycji
  }

  const getExpiryText = (days: number) => {
    if (days === 0) return 'Dzisiaj';
    if (days === 1) return 'Jutro';
    if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'dzień' : 'dni'} temu`;
    return `Za ${days} ${days === 1 ? 'dzień' : 'dni'}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Boks: Wygasłe */}
      {expiredItems.length > 0 && (
        <Alert className="border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <AlertTitle className="text-destructive font-semibold text-sm">
            ⚠️ Wymagane działanie
          </AlertTitle>
          <AlertDescription className="mt-3">
            <h3 className="text-xs font-bold text-destructive mb-2 uppercase tracking-wide">
              Wygasłe ({expiredItems.length})
            </h3>
            <div className="space-y-1">
              {expiredItems.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="flex items-center justify-between p-2 bg-background/50 rounded border border-destructive/20 text-xs hover:bg-background/70 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge variant="destructive" className="text-xs font-mono px-1.5 py-0 shrink-0">
                      {item.type}
                    </Badge>
                    <span className="font-medium text-foreground truncate" title={item.name}>
                      {item.name}
                    </span>
                    {item.category && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">
                        {item.category}
                      </Badge>
                    )}
                  </div>
                  <div className="text-destructive text-xs font-semibold shrink-0 ml-2">
                    {getExpiryText(item.daysUntilExpiry)}
                  </div>
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Boks: Wygasające w ciągu 14 dni */}
      {expiringSoonItems.length > 0 && (
        <Alert className="border-warning/50 bg-warning/10">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning font-semibold text-sm">
            ⏰ Wygasające wkrótce
          </AlertTitle>
          <AlertDescription className="mt-3">
            <h3 className="text-xs font-bold text-warning mb-2 uppercase tracking-wide">
              Wygasające w ciągu 14 dni ({expiringSoonItems.length})
            </h3>
            <div className="space-y-1">
              {expiringSoonItems.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="flex items-center justify-between p-2 bg-background/50 rounded border border-warning/20 text-xs hover:bg-background/70 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs font-mono px-1.5 py-0 shrink-0',
                        item.daysUntilExpiry === 0 && 'border-destructive text-destructive',
                        item.daysUntilExpiry === 1 && 'border-warning text-warning'
                      )}
                    >
                      {item.type}
                    </Badge>
                    <span className="font-medium text-foreground truncate" title={item.name}>
                      {item.name}
                    </span>
                    {item.category && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">
                        {item.category}
                      </Badge>
                    )}
                  </div>
                  <div
                    className={cn(
                      'text-xs font-semibold shrink-0 ml-2',
                      item.daysUntilExpiry === 0 && 'text-destructive',
                      item.daysUntilExpiry === 1 && 'text-warning',
                      item.daysUntilExpiry > 1 && 'text-warning'
                    )}
                  >
                    {getExpiryText(item.daysUntilExpiry)}
                  </div>
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ExpiryAlertBanner;

